/**
 * Dialog and Sheet — roadmap 4.2.
 *
 * Both are the same Radix primitive with different geometry: a dialog is
 * centred, a sheet slides in from the inline end. Radix owns the focus trap, the
 * scroll lock, the `Escape` handling and the `aria-modal` wiring — all of which
 * are easy to write badly and expensive to debug later.
 *
 * The sheet enters from `end`, so it comes from the right in English and from
 * the left in Arabic.
 */
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from './cn'

/**
 * Pass-throughs rather than `export const X = DialogPrimitive.X`.
 *
 * A re-exported identifier is not recognisable as a component, so Fast Refresh
 * gives up on the whole module — and `--max-warnings=0` makes that a build
 * failure. `ref` is an ordinary prop in React 19, so spreading forwards it and
 * Radix's `asChild` composition still works.
 */
export function DialogRoot(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />
}

export function DialogTrigger(props: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger {...props} />
}

export function DialogClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close {...props} />
}

function Overlay() {
  return <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-overlay backdrop-blur-[2px]" />
}

export type DialogContentProps = {
  title: string
  /**
   * Required, not optional. Radix warns without a description, and more to the
   * point a modal that only says "Are you sure?" does not tell the user what
   * they are agreeing to.
   */
  description: string
  closeLabel: string
  footer?: React.ReactNode
  className?: string | undefined
  children?: React.ReactNode
}

export function DialogContent({
  title,
  description,
  closeLabel,
  footer,
  className,
  children,
}: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <Overlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed start-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
          // ⚠️ **Capped and column-laid, so the footer is always reachable.**
          // Without this a dialog taller than the viewport overflowed *both*
          // edges — it is centred, so growth goes up and down at once — and its
          // own submit button sat off-screen with no scrollbar to suggest it.
          // Nothing failed loudly: the button stayed in the DOM and enabled, so
          // the form looked complete and simply could not be submitted. The
          // body below is what scrolls; the header and footer stay put.
          // `SheetContent` has always done this. This one had not.
          'flex max-h-[calc(100dvh-2rem)] flex-col',
          'rounded-xl border border-line bg-raised shadow-xl ring-1 ring-edge',
          // The translate above is physical by necessity — `start-1/2` positions
          // from the inline start, and pulling back by half the width is the
          // same maths in both directions.
          'rtl:translate-x-1/2',
          className,
        )}
      >
        <div className="flex shrink-0 items-start gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-base font-semibold">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-1 text-sm text-fg-muted">
              {description}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close
            aria-label={closeLabel}
            className="rounded-md p-1 text-fg-subtle hover:bg-sunken hover:text-fg"
          >
            <X aria-hidden="true" className="size-4" />
          </DialogPrimitive.Close>
        </div>

        {/* `min-h-0` is what actually lets this shrink: a flex child's default
            `min-height:auto` refuses to go below its content, so the cap above
            would push the footer out instead of scrolling here. */}
        {children ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        ) : null}

        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-line px-5 py-4">
            {footer}
          </div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

export type SheetContentProps = DialogContentProps & {
  /**
   * Which inline edge the panel is anchored to. Both values are *logical*, so
   * each flips with the writing direction: `start` is the left in English and
   * the right in Arabic.
   *
   * **Pick the edge the trigger is on.** A drawer that opens on the far side of
   * the screen from the button just pressed makes the user's eye travel the
   * width of the viewport, and in a mirrored layout it lands where they were
   * not looking. The navigation drawer is `start`, because the hamburger is at
   * the inline start; a filter or editor panel opened from a toolbar on the
   * far side stays `end`.
   */
  side?: 'start' | 'end' | undefined
}

/** A panel anchored to an inline edge — for navigation, filters and editors. */
export function SheetContent({
  title,
  description,
  closeLabel,
  footer,
  className,
  children,
  side = 'end',
}: SheetContentProps) {
  return (
    <DialogPrimitive.Portal>
      <Overlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-0 z-50 flex h-dvh w-full max-w-md flex-col',
          // The border goes on the edge facing the page, which is the opposite
          // one from the anchor — otherwise it draws against the viewport edge
          // where nothing can see it.
          side === 'start' ? 'start-0 border-e' : 'end-0 border-s',
          'border-line bg-raised shadow-xl ring-1 ring-edge',
          className,
        )}
      >
        <div className="flex items-start gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-base font-semibold">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="mt-1 text-sm text-fg-muted">
              {description}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close
            aria-label={closeLabel}
            className="rounded-md p-1 text-fg-subtle hover:bg-sunken hover:text-fg"
          >
            <X aria-hidden="true" className="size-4" />
          </DialogPrimitive.Close>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-line px-5 py-4">
            {footer}
          </div>
        ) : null}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}
